/**
 * git-grasp-pool — content-addressed git object cache
 *
 * Two-tier cache: L1 in-memory (Map) for zero-latency synchronous peeks,
 * backed by L2 IndexedDB for cross-tab persistence and page-reload survival.
 *
 * Git objects are immutable and globally unique by SHA-1 hash, so entries
 * never need invalidation — except infoRefs which has a configurable TTL.
 *
 * The cache is shared across all pool instances via a singleton IDB database.
 * L1 memory caches are module-level so they survive pool disposal (the data
 * is still valid for any future pool with the same URLs).
 */

import type {
  Commit,
  Tree,
  InfoRefsUploadPackResponse,
  ParsedObject,
} from "@/lib/vendored/git-natural-api";

// ---------------------------------------------------------------------------
// IndexedDB setup
// ---------------------------------------------------------------------------

// DB_VERSION 3: added "trees" and "commitHistory" stores.
const DB_NAME = "gitworkshop-git-cache";
const DB_VERSION = 3;

const STORE_COMMITS = "commits";
const STORE_BLOBS = "blobs";
const STORE_INFO_REFS = "infoRefs";
const STORE_TREES = "trees";
const STORE_COMMIT_HISTORY = "commitHistory";
const COMMIT_HISTORY_CACHE_VERSION = 2;

/** Default TTL for infoRefs entries */
export const DEFAULT_INFO_REFS_TTL_MS = 60_000; // 1 minute

/**
 * Sentinel nestLimit used when caching a fully-recursive tree parse.
 * Any request for nestLimit ≤ FULL_NEST_LIMIT is satisfied by this entry.
 */
export const FULL_NEST_LIMIT = 999;

/**
 * Raw packfile objects for a commit (blob:none fetch).
 * Stored L1-only — derived data that can be re-fetched cheaply.
 */
export interface RawObjectsEntry {
  rootTreeHash: string;
  objects: Map<string, ParsedObject>;
}

// IDB record types
interface CommitRecord {
  hash: string;
  commit: Commit;
}

interface BlobRecord {
  hash: string;
  data: Uint8Array;
}

interface InfoRefsRecord {
  url: string;
  info: InfoRefsUploadPackResponse;
  fetchedAt: number;
}

interface TreeRecord {
  key: string; // `${commitHash}:${nestLimit}`
  tree: Tree;
}

interface CommitHistoryRecord {
  key: string; // `v${COMMIT_HISTORY_CACHE_VERSION}:${commitHash}:${maxCommits}`
  commits: Commit[];
}

function commitHistoryKey(commitHash: string, maxCommits: number) {
  return `v${COMMIT_HISTORY_CACHE_VERSION}:${commitHash}:${maxCommits}`;
}

// ---------------------------------------------------------------------------
// IDB helpers
// ---------------------------------------------------------------------------

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Whether IndexedDB is available in the current environment.
 *
 * It is absent in non-browser runtimes (Node, SSR, the e2e test harness).
 * When unavailable, the L2 (IDB) tier silently degrades to a no-op and the
 * cache operates L1-only — every git operation still works, just without
 * cross-session persistence.
 */
const idbAvailable = typeof indexedDB !== "undefined";

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_COMMITS))
        db.createObjectStore(STORE_COMMITS, { keyPath: "hash" });
      if (!db.objectStoreNames.contains(STORE_BLOBS))
        db.createObjectStore(STORE_BLOBS, { keyPath: "hash" });
      if (!db.objectStoreNames.contains(STORE_INFO_REFS))
        db.createObjectStore(STORE_INFO_REFS, { keyPath: "url" });
      if (!db.objectStoreNames.contains(STORE_TREES))
        db.createObjectStore(STORE_TREES, { keyPath: "key" });
      if (!db.objectStoreNames.contains(STORE_COMMIT_HISTORY))
        db.createObjectStore(STORE_COMMIT_HISTORY, { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

function idbGet<T>(storeName: string, key: string): Promise<T | undefined> {
  if (!idbAvailable) return Promise.resolve(undefined);
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const req = tx.objectStore(storeName).get(key);
        req.onsuccess = () => resolve(req.result as T | undefined);
        req.onerror = () => reject(req.error);
      }),
  );
}

function idbPut(storeName: string, value: unknown): Promise<void> {
  if (!idbAvailable) return Promise.resolve();
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const req = tx.objectStore(storeName).put(value);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
  );
}

function idbDelete(storeName: string, key: string): Promise<void> {
  if (!idbAvailable) return Promise.resolve();
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const req = tx.objectStore(storeName).delete(key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
  );
}

// ---------------------------------------------------------------------------
// L1 in-memory caches (module-level singletons)
// ---------------------------------------------------------------------------

const memCommits = new Map<string, Commit>();
const memBlobs = new Map<string, Uint8Array>();
/** key: `${commitHash}:${path}` → decoded text */
const memTexts = new Map<string, string>();
/** key: `${commitHash}:${nestLimit}` → Tree */
const memTrees = new Map<string, Tree>();
/** key: cloneUrl → { info, fetchedAt } */
const memInfoRefs = new Map<
  string,
  { info: InfoRefsUploadPackResponse; fetchedAt: number }
>();
/**
 * URLs explicitly invalidated at a mutation/provisioning boundary.
 *
 * IDB deletion is asynchronous, so this synchronous guard prevents the next
 * fetch from immediately repopulating L1 with the stale L2 advertisement.
 */
const invalidatedInfoRefs = new Set<string>();
/** key: `${commitHash}:${maxCommits}` → Commit[] */
const memCommitHistory = new Map<string, Commit[]>();
/**
 * L1-only raw packfile objects cache, keyed by commitHash.
 * Not persisted to IDB — raw objects are reconstructable from a single HTTP
 * request and are only useful within the current page session.
 */
const memRawObjects = new Map<string, RawObjectsEntry>();

// ---------------------------------------------------------------------------
// GitObjectCache — the public API
// ---------------------------------------------------------------------------

/**
 * Content-addressed git object cache with L1 memory + L2 IDB tiers.
 *
 * All methods are safe to call from any pool instance. The underlying
 * storage is shared (module-level maps + singleton IDB).
 */
export class GitObjectCache {
  private infoRefsTtlMs: number;

  constructor(infoRefsTtlMs: number = DEFAULT_INFO_REFS_TTL_MS) {
    this.infoRefsTtlMs = infoRefsTtlMs;
  }

  // -----------------------------------------------------------------------
  // Commits
  // -----------------------------------------------------------------------

  /** Synchronous L1-only peek */
  peekCommit(hash: string): Commit | undefined {
    return memCommits.get(hash);
  }

  /** L1 then L2 */
  async getCommit(hash: string): Promise<Commit | undefined> {
    const mem = memCommits.get(hash);
    if (mem) return mem;
    const record = await idbGet<CommitRecord>(STORE_COMMITS, hash);
    if (record) {
      memCommits.set(hash, record.commit);
      return record.commit;
    }
    return undefined;
  }

  /** Store in both L1 and L2 */
  putCommit(commit: Commit): void {
    memCommits.set(commit.hash, commit);
    idbPut(STORE_COMMITS, { hash: commit.hash, commit }).catch(() => {});
  }

  // -----------------------------------------------------------------------
  // Blobs
  // -----------------------------------------------------------------------

  peekBlob(hash: string): Uint8Array | undefined {
    return memBlobs.get(hash);
  }

  async getBlob(hash: string): Promise<Uint8Array | undefined> {
    const mem = memBlobs.get(hash);
    if (mem) return mem;
    const record = await idbGet<BlobRecord>(STORE_BLOBS, hash);
    if (record) {
      memBlobs.set(hash, record.data);
      return record.data;
    }
    return undefined;
  }

  putBlob(hash: string, data: Uint8Array): void {
    memBlobs.set(hash, data);
    idbPut(STORE_BLOBS, { hash, data }).catch(() => {});
  }

  // -----------------------------------------------------------------------
  // Text (derived from blobs — L1 only, no IDB)
  // -----------------------------------------------------------------------

  getText(commitHash: string, path: string): string | undefined {
    return memTexts.get(`${commitHash}:${path}`);
  }

  putText(commitHash: string, path: string, text: string): void {
    memTexts.set(`${commitHash}:${path}`, text);
  }

  // -----------------------------------------------------------------------
  // InfoRefs (URL-scoped, TTL-based)
  // -----------------------------------------------------------------------

  /** Synchronous L1-only peek, respecting TTL */
  peekInfoRefs(url: string): InfoRefsUploadPackResponse | undefined {
    const mem = memInfoRefs.get(url);
    if (mem && Date.now() - mem.fetchedAt < this.infoRefsTtlMs) return mem.info;
    return undefined;
  }

  /** Synchronous L1-only peek, ignoring TTL (for fast-path rendering) */
  peekInfoRefsStale(url: string): InfoRefsUploadPackResponse | undefined {
    return memInfoRefs.get(url)?.info;
  }

  /** L1 then L2, respecting TTL */
  async getInfoRefs(
    url: string,
  ): Promise<InfoRefsUploadPackResponse | undefined> {
    const mem = memInfoRefs.get(url);
    if (mem && Date.now() - mem.fetchedAt < this.infoRefsTtlMs) return mem.info;
    if (invalidatedInfoRefs.has(url)) return undefined;
    const record = await idbGet<InfoRefsRecord>(STORE_INFO_REFS, url);
    if (record && Date.now() - record.fetchedAt < this.infoRefsTtlMs) {
      memInfoRefs.set(url, { info: record.info, fetchedAt: record.fetchedAt });
      return record.info;
    }
    return undefined;
  }

  putInfoRefs(url: string, info: InfoRefsUploadPackResponse): void {
    const fetchedAt = Date.now();
    invalidatedInfoRefs.delete(url);
    memInfoRefs.set(url, { info, fetchedAt });
    idbPut(STORE_INFO_REFS, { url, info, fetchedAt }).catch(() => {});
  }

  /** Invalidate a specific URL's infoRefs (e.g. after a known push) */
  invalidateInfoRefs(url: string): void {
    memInfoRefs.delete(url);
    invalidatedInfoRefs.add(url);
    idbDelete(STORE_INFO_REFS, url).catch(() => {});
  }

  // -----------------------------------------------------------------------
  // Trees (content-addressed, no TTL)
  // -----------------------------------------------------------------------

  peekTree(commitHash: string, nestLimit: number): Tree | undefined {
    // Exact match (O(1) fast path)
    const exact = memTrees.get(`${commitHash}:${nestLimit}`);
    if (exact) return exact;
    // Scan for any entry with a deeper parse that satisfies this request.
    // The "full:${commitHash}" diff-cache entries use a different prefix and
    // are never matched here.
    const prefix = `${commitHash}:`;
    for (const [key, tree] of memTrees) {
      if (!key.startsWith(prefix)) continue;
      const cachedLimit = parseInt(key.slice(prefix.length), 10);
      if (!isNaN(cachedLimit) && cachedLimit >= nestLimit) return tree;
    }
    return undefined;
  }

  async getTree(
    commitHash: string,
    nestLimit: number,
  ): Promise<Tree | undefined> {
    // L1 scan with >= check (also warms L1 if a deeper entry is found)
    const memHit = this.peekTree(commitHash, nestLimit);
    if (memHit) return memHit;

    // IDB exact key
    const exactKey = `${commitHash}:${nestLimit}`;
    const exactRecord = await idbGet<TreeRecord>(STORE_TREES, exactKey);
    if (exactRecord) {
      memTrees.set(exactKey, exactRecord.tree);
      return exactRecord.tree;
    }

    // IDB FULL_NEST_LIMIT key — background full-parse result survives page reloads
    if (nestLimit < FULL_NEST_LIMIT) {
      const fullKey = `${commitHash}:${FULL_NEST_LIMIT}`;
      const fullRecord = await idbGet<TreeRecord>(STORE_TREES, fullKey);
      if (fullRecord) {
        memTrees.set(fullKey, fullRecord.tree);
        return fullRecord.tree;
      }
    }

    return undefined;
  }

  putTree(commitHash: string, nestLimit: number, tree: Tree): void {
    const k = `${commitHash}:${nestLimit}`;
    memTrees.set(k, tree);
    idbPut(STORE_TREES, { key: k, tree }).catch(() => {});
  }

  // -----------------------------------------------------------------------
  // Full trees (for diff — complete recursive trees, no TTL)
  //
  // Stored under a namespaced key "full:<commitHash>" to avoid any collision
  // with the file-browser tree cache which uses "<commitHash>:<nestLimit>".
  // -----------------------------------------------------------------------

  peekFullTree(commitHash: string): Tree | undefined {
    return memTrees.get(`full:${commitHash}`);
  }

  async getFullTree(commitHash: string): Promise<Tree | undefined> {
    const k = `full:${commitHash}`;
    const mem = memTrees.get(k);
    if (mem) return mem;
    const record = await idbGet<TreeRecord>(STORE_TREES, k);
    if (record) {
      memTrees.set(k, record.tree);
      return record.tree;
    }
    return undefined;
  }

  putFullTree(commitHash: string, tree: Tree): void {
    const k = `full:${commitHash}`;
    memTrees.set(k, tree);
    idbPut(STORE_TREES, { key: k, tree }).catch(() => {});
  }

  // -----------------------------------------------------------------------
  // Raw packfile objects (L1 only — no IDB, no TTL)
  //
  // Stores the blob:none packfile objects map for a commit so that subsequent
  // tree requests at different depths can re-parse from memory instead of
  // re-fetching from the network.
  // -----------------------------------------------------------------------

  /** Synchronous L1-only peek */
  peekRawObjects(commitHash: string): RawObjectsEntry | undefined {
    return memRawObjects.get(commitHash);
  }

  /** Store raw objects for a commit (L1 only) */
  putRawObjects(commitHash: string, entry: RawObjectsEntry): void {
    memRawObjects.set(commitHash, entry);
  }

  // -----------------------------------------------------------------------
  // Commit history (content-addressed, no TTL)
  // -----------------------------------------------------------------------

  peekCommitHistory(
    commitHash: string,
    maxCommits: number,
  ): Commit[] | undefined {
    return memCommitHistory.get(commitHistoryKey(commitHash, maxCommits));
  }

  async getCommitHistory(
    commitHash: string,
    maxCommits: number,
  ): Promise<Commit[] | undefined> {
    const k = commitHistoryKey(commitHash, maxCommits);
    const mem = memCommitHistory.get(k);
    if (mem) return mem;
    const record = await idbGet<CommitHistoryRecord>(STORE_COMMIT_HISTORY, k);
    if (record) {
      memCommitHistory.set(k, record.commits);
      return record.commits;
    }
    return undefined;
  }

  putCommitHistory(
    commitHash: string,
    maxCommits: number,
    commits: Commit[],
  ): void {
    const k = commitHistoryKey(commitHash, maxCommits);
    memCommitHistory.set(k, commits);
    idbPut(STORE_COMMIT_HISTORY, { key: k, commits }).catch(() => {});
  }
}
